import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AxiosInstance } from 'axios';

import { useApi } from '../../../services/hooks/apiHook';
import { errorToast } from '../../../utils/toasts';
import { useDialogsStore } from '../DashboardApprovalDocument/store/dialogs.store';
import { errorMessageAdapter } from '../../../adapters';
import { useAuth } from '../../../store/auth';
import { FolderService } from '../../../services/FolderService';
import { BsEye } from 'react-icons/bs';
import { FiRefreshCcw } from 'react-icons/fi';

export interface StatusRow {
  documentName: string;
  signerType: 'cliente' | 'diretor' | 'shareholder' | 'agente' | '-';
  signerName: string;
  status: 'pending' | 'signed' | 'rejected';
  incorporationDate: string;
  url: string
}

export const DashBoardDocumentStatus: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const api: AxiosInstance = useApi();
  const { token } = useAuth();

  // estados de paginação
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [statuses, setStatuses] = useState<StatusRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { setShareholderSlug, setDocumentSlug, setDocumentUrl } = useDialogsStore();

  const mapSignerType = (type: string): StatusRow['signerType'] => {
    switch (type) {
      case 'customer': return 'cliente';
      case 'directors': return 'diretor';
      case 'shareholders': return 'shareholder';
      case 'agent': return 'agente';
      default: return '-';
    }
  };

  const getStatusLabel = {
    pending: t('others.othPending'),
    signed: t('titles.ttlSigned'),
    rejected: t('others.othRejected'),
  } as const;

  const getTagClasses = {
    pending: 'bg-yellow-100 text-yellow-800',
    signed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  } as const;

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await FolderService.getCompanyAllDocuments(
        api,
        slug,
        {
          headers: {
            'Content-Language': i18n.language.toLowerCase(),
            Authorization: `Bearer ${token}`
          },
          params: {
            page,
          }
        }
      );

      console.log('resposta: ', response);


      // atualiza estado de paginação
      if (response.pagination) setLastPage(response.pagination.last_page);

      // transforma cada assinatura em uma linha única
      // const rows: StatusRow[] = (response.items ?? []).flatMap((item: any) =>
      //   (item.signatures || []).map((sig: any) => ({
      //     documentName: item.name,
      //     signerType: mapSignerType(sig.type),
      //     signerName: sig.name,
      //     status: sig.status,
      //     incorporationDate: item.created_at,
      //     url: `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${item.s3path}`
      //   }))
      // );

      const rows: StatusRow[] = (response.items ?? []).map((item: any) => {
        // Exemplo: pegando a primeira assinatura para preencher os campos.
        // Se não precisar de detalhes da assinatura, esses campos podem ser vazios ou "N/A".
        const firstSignature = item.signatures && item.signatures.length > 0
          ? item.signatures[0]
          : null;

        return {
          documentName: item.name,
          signerType: firstSignature ? mapSignerType(firstSignature.type) : '-',
          signerName: firstSignature ? firstSignature.name : '-',
          status: firstSignature ? firstSignature.status: '-',
          incorporationDate: item.issue_date,
          url: `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${item.s3path}`
        };
      });

      setStatuses(rows);

      console.log('***', statuses);

    } catch (e: any) {
      errorToast(errorMessageAdapter(e));
    } finally {
      setLoading(false);
    }
  };

  // refaz a busca sempre que mudar page ou slug
  useEffect(() => {
    fetchDocuments();
  }, [slug, page]);

  return (
    <div className="overflow-x-auto">
      <div className="flex w-full flex-col border-b-[2px] pb-3 mb-6">
        <h2 className="text-xl font-bold text-[#0C1FAA]">{t('others.othDocumentStatus')}</h2>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="mt-8 min-w-full bg-white rounded-lg shadow">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-2">{t('labels.lblDocument')}</th>
                <th className="px-4 py-2">{t('labels.lblSubscriberType')}</th>
                <th className="px-4 py-2">{t('labels.lblSubscriberName')}</th>
                <th className="px-4 py-2">{t('labels.lblStatus')}</th>
                <th className="px-4 py-2">{t('labels.lblIssueDateLess')}</th>
                <th className="px-4 py-2">{t('labels.lblActions')}</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((row, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-3">{row.documentName}</td>
                  <td className="px-4 py-3 capitalize">{row.signerType}</td>
                  <td className="px-4 py-3">{row.signerName}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getTagClasses[row.status]}`}>
                      {getStatusLabel[row.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {format(new Date(row.incorporationDate), 'dd/MM/yyyy')}
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    {/* <button className="text-blue-600 hover:underline text-sm" title='Reenviar'><FiRefreshCcw size={18} /></button> */}
                    <button className="text-blue-600 hover:underline text-sm"
                      title='Visualizar'
                      onClick={() => {
                        setPreviewUrl(row.url);
                        setIsPreviewOpen(true);
                      }}
                    >
                      <BsEye size={20} />  
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* controles de paginação */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              {t('OnboardingCreateAccountButtonBack')}
            </button>

            <span>
              {t('labels.lblPage')} {page} {t('others.othOfTable')} {lastPage}
            </span>

            <button
              onClick={() => setPage(prev => Math.min(prev + 1, lastPage))}
              disabled={page === lastPage}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              {t('OnboardingCreateAccountButtonNext')}
            </button>
          </div>

          {/* Modal de visualização */}
          {isPreviewOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={() => setIsPreviewOpen(false)} // fecha clicando fora
            >
              <div
                className="bg-white w-3/4 h-3/4 rounded-lg shadow-lg relative"
                onClick={e => e.stopPropagation()} // impede o clique interno de fechar
              >
                {/* Botão de fechar */}

                {/* Visor do PDF */}
                <iframe
                  src={previewUrl!}
                  className="w-full h-full rounded-b-lg"
                  title="Visualização de Documento"
                />
              </div>

              <button
                className="absolute top-2 right-2 text-white bg-gray-700 rounded p-3 hover:text-gray-900"
                onClick={() => setIsPreviewOpen(false)}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
